import { fetchSubProductsOrNominals } from '@api/useQueryCalls';
import { View } from '@components/primitives';
import useFavourites from '@customHooks/useFavourites';
import { useToast } from '@hooks';
import { useNavigation, RouteProp } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import { showErrorOnAPI } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showListOfProducts } from './subProductsUtils';
import { shouldShowAccountInput, shouldShowPhoneSelection, shouldShowTitle } from '../BPUtils';
import { logPageBack, logPageLaunch } from '../GGNominals/analyticsNominals';

// ---------- TYPES ----------
interface ProductConfig {
  productId: number;
  productName: string;
  noOfColumns?: number;
  hasPhoneChange?: boolean;
}

interface RouteParams {
  source: string;
  productConfig: ProductConfig;
  productApiData: Record<string, any>;
}

type GGSubProductsRoute = RouteProp<{ params: RouteParams }, 'params'>;

interface GGSubProductsProps {
  route: GGSubProductsRoute;
}

// ---------- COMPONENT ----------
const GGSubProducts: React.FC<GGSubProductsProps> = ({ route }) => {
  const { bpPhone, setLoading, setInitialData, selectedBPAccount, validatedReferralCode } = getStore();
  const { mobileNumber } = bpPhone;
  const { t } = useTranslation('common');
  const toast = useToast();
  const navigation = useNavigation();
  const { accountTypeId } = selectedBPAccount;

  const { productConfig, productApiData, source } = route.params;
  const { productId, productName } = productConfig;

  const { isLoading, isSuccess, isError, error, refetch, data } = fetchSubProductsOrNominals({
    billAccountNumber: mobileNumber,
    productId,
    accountTypeId,
  });

  const isFavourite = useFavourites(
    navigation,
    productApiData?.id,
    productApiData?.isDefaultFavorites || productApiData?.isFavorites,
    source,
    productApiData?.prefix,
    productApiData?.canAddFavorites,
  );

  const [subProducts, setSubProductsData] = useState<string[]>([]);
  const [productNominals, setProductNominals] = useState<Record<string, any[]>>({});
  const [selectedProductItem, setSelectedProductItem] = useState<string>('');

  useEffect(() => {
    navigation.setOptions?.({ title: productName });
  }, [productName]);

  useEffect(() => {
    logPageLaunch(productConfig, source, validatedReferralCode, isFavourite);
    refetch();
    return () => {
      logPageBack(productConfig, source, validatedReferralCode);
    };
  }, []);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    showErrorOnAPI(isError, error, toast, t, navigation, setInitialData);
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess && data) {
      const keys = Object.keys(data);
      setSubProductsData(keys);
      setProductNominals(data);
    }
  }, [isSuccess, data]);

  return (
    <View style={appStyles.safeAreaView}>
      {shouldShowPhoneSelection(route.params)}
      {shouldShowAccountInput({ productConfig, productApiData })}
      {shouldShowTitle(productConfig)}
      {showListOfProducts({
        subProducts,
        productNominals,
        selectedProductItem,
        setSelectedProductItem,
        productId,
        productConfig,
        productApiData,
        source,
        isFavourite,
      })}
    </View>
  );
};

export default GGSubProducts;
